import { useMemo } from "react";

import { UIView, useCurrentStateAndParams } from "@uirouter/react";
import { startCase } from "lodash";
import { ErrorBoundary } from "react-error-boundary";
import styled from "styled-components";

import { useAllKeywordGroups } from "app/services/keyword-groups/hooks/useKeywordGroups";
import { adaptKeywordsQueryBarProps } from "app/components/compare/KeywordsQueryBar/KeywordsQueryBarHelper";
import type { QueryBarItemMode } from "app/components/compare/KeywordsQueryBar/KeywordsQueryBarTypes";
import { ErrorFallback } from "app/components/error-boundary-no-data/ErrorBoundaryEmptyState";
import PageTitle from "app/components/React/PageTitle/PageTitle";
import { InfoBanner } from "app/components/React/PlaResearch/InfoBanner";
import { SubNav } from "app/components/sub-navigation-bar/SubNav";
import { useAutoScrollTop } from "app/custom-hooks/useAutoScrollTop";
import { translate } from "app/filters/i18nFilter";
import { BaseFlex } from "pro-features/styled components/StyledFlex/src/StyledFlex";
import { DEFAULT_PAGE_LAYOUT_CLASS_NAME } from "app/UtilitiesAndConstants/Constants/css";

import { MonitorKeywordsFiltersInner } from "./MonitorKeywordsFilters";
import { MonitorKeywordsQueryBar } from "./MonitorKeywordsQueryBar";

const HeaderFiltersContainer = styled(BaseFlex)`
    justify-content: end;
    flex: 1;
`;

const FrameContainer = styled.section`
    height: 100%;
    display: flex;
`;

const FrameWrapper = styled.span`
    flex-grow: 1;
    overflow-x: hidden;
    width: 100%;
`;

const PageInnerContainer = styled.div`
    padding-top: 0;
`;

const ErrorBoundaryWrapper = styled.div`
    margin-top: 24px;
`;

export const MonitorKeywordsFrame = () => {
    const { params, state } = useCurrentStateAndParams();
    const ref = useAutoScrollTop();
    const { userGroups, sharedGroups } = useAllKeywordGroups();

    const queryBarProps = useMemo(() => {
        const customCategories = [userGroups, sharedGroups];
        const props: { customCategories: any[]; selectedCat: string; mode: QueryBarItemMode } = {
            ...params,
            customCategories,
            selectedCat: params.keywordGroupId,
            mode: "keywordGroup",
        };
        return { ...adaptKeywordsQueryBarProps(props), showKeywords: false, showButtons: false };
    }, [params, userGroups, sharedGroups]);

    const pageLayoutClassName = state.pageLayoutClassName ?? DEFAULT_PAGE_LAYOUT_CLASS_NAME;
    const keywordGroupName = queryBarProps.keywordGroups.find((group) => group.id === params.keywordGroupId)?.text;
    const pageTitle = translate("keyword.analysis.monitor.keywords.page.title", {
        keywordGroupName: startCase(keywordGroupName),
    });

    return (
        <ErrorBoundary
            FallbackComponent={ErrorFallback}
            resetKeys={[state, params]}
        >
            <FrameContainer className="keyword-analysis-search sw-layout-no-scroll-container">
                <FrameWrapper className="u-flex-row u-flex-column">
                    <SubNav
                        topLeftComponent={<MonitorKeywordsQueryBar queryBarProps={queryBarProps} />}
                        topRightComponent={
                            <HeaderFiltersContainer className="subNav-filters">
                                <MonitorKeywordsFiltersInner />
                            </HeaderFiltersContainer>
                        }
                        bottomLeftComponent={<PageTitle title={pageTitle} />}
                    />
                    <InfoBanner
                        displayInPage="paidsearch_keywordAnalysis_serp_ads"
                        userDataBannerKey="userDataBannerKey"
                        text="keyword.analysis.plaResearch.page.toolTip.text"
                        title="keyword.analysis.plaResearch.page.toolTip.title"
                    />
                    <PageInnerContainer
                        ref={ref}
                        className="sw-layout-scrollable-element sw-layout-research use-sticky-css-rendering"
                    >
                        <ErrorBoundaryWrapper className={`sw-page-websiteAnalysis ${pageLayoutClassName}`}>
                            <ErrorBoundary
                                FallbackComponent={ErrorFallback}
                                resetKeys={[state, params]}
                            >
                                <UIView />
                            </ErrorBoundary>
                        </ErrorBoundaryWrapper>
                    </PageInnerContainer>
                </FrameWrapper>
            </FrameContainer>
        </ErrorBoundary>
    );
};
